import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import CarbonDioxideLabel from 'classes/StandingData/CarbonDioxideLabel/Search/CarbonDioxideLabel';
import CarbonDioxideLabelList from 'classes/StandingData/CarbonDioxideLabel/Search/CarbonDioxideLabelList';
import Search from 'classes/StandingData/CarbonDioxideLabel/Search/Search';
import CarbonDioxideLabelDetailComponent from 'components/desktop/StandingData/CarbonDioxideLabel/Detail/CarbonDioxideLabelDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const CarbonDioxideLabelManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxValueScope = useSelector((state: RootState) => state.valueScope);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [carbonDioxideLabelList, setCarbonDioxideLabelList] = useState<CarbonDioxideLabelList>(
		new CarbonDioxideLabelList()
	);
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getCarbonDioxideLabel();
		} else {
			await getCarbonDioxideLabel();
		}
		setSearch(clone(search));
	};

	const handleRestoreCarbonDioxideLabel = async (
		carbonDioxideLabel: CarbonDioxideLabel
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:CARBONDIOXIDELABEL_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await carbonDioxideLabel.callApiRestore();
				await Swal.fire({
					title: t('standingData:CARBONDIOXIDELABEL_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getCarbonDioxideLabel();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const handleArchiveCarbonDioxideLabel = async (
		carbonDioxideLabel: CarbonDioxideLabel
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:CARBONDIOXIDELABEL_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await carbonDioxideLabel.callApiDelete();
				await Swal.fire({
					title: t('standingData:CARBONDIOXIDELABEL_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getCarbonDioxideLabel();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const getCarbonDioxideLabel = async () => {
		setIsLoading(true);
		try {
			const response = await carbonDioxideLabelList.callApi(search.mapToRequest());
			if (response.hasValue()) {
				carbonDioxideLabelList.mapFromApi(response.get());
				setCarbonDioxideLabelList(clone(carbonDioxideLabelList));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getCarbonDioxideLabel();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getSuggestedCarbonDioxideLabels = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await carbonDioxideLabelList.search(
				searchTerm,
				reduxValueScope,
				props.archive ?? false
			);
		} else {
			carbonDioxideLabelList.filtered = [];
		}

		setCarbonDioxideLabelList(clone(carbonDioxideLabelList));
		return carbonDioxideLabelList.filtered;
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataArchive = (input: CarbonDioxideLabel): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveCarbonDioxideLabel(input),
			};
		}
	};

	const getButtonDataRestore = (input: CarbonDioxideLabel): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreCarbonDioxideLabel(input),
			};
		}
	};

	const renderButtons = (input: CarbonDioxideLabel): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return carbonDioxideLabelList.all.map((label: CarbonDioxideLabel) => {
			return {
				id: label.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div style={{ width: '50px' }}>{label.label}</div>
						{label.maxValue && (
							<div className="flex" style={{ width: '200px' }}>
								<span className="grey">{t('standingData:MAX_VALUE')}: </span>
								{label.maxValue}
							</div>
						)}
						<div className="flex-fill">
							<span className="grey">{t('standingData:COLOR')}: </span>
							<FontAwesomeIcon color={label.color} icon={faCircle as IconProp} />{' '}
							{label.color}
						</div>
						<div>{renderButtons(label)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = (): JSX.Element => {
		return (
			<main className="container">
				<div className="d-flex flex-row justify-content-between">
					{!props.archive ? (
						<>
							<h1>{t('standingData:CARBONDIOXIDELABEL_MANAGEMENT')}</h1>
							<div className="d-flex flex-gap-25">
								<BtnNew
									onClick={handleChangeIdToNew}
									name={t('standingData:CREATE_CARBONDIOXIDELABEL')}
								/>
							</div>
						</>
					) : (
						<h1>{t('standingData:CARBONDIOXIDELABEL_ARCHIVE')}</h1>
					)}
				</div>
				<br />
				<div className="row">
					<div className="col-lg-12">
						<div className="input-group mb-3">
							<AdvancedSearchInput
								debounceMilliSeconds={350}
								getSuggestedItems={getSuggestedCarbonDioxideLabels}
								handleSearch={handleSearch}
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
								triggerSearchLetterAmount={1}
							/>
						</div>
					</div>
				</div>
				<br />
				<LoadingAnimation isLoading={isLoading} />
				<p>{carbonDioxideLabelList.totalCount + ' ' + t('_general:CARBONDIOXIDELABELS')}</p>
				<StandingDataList
					archive={props.archive}
					displayOverlay={displayOverlay}
					rows={renderListRows()}
					overlay={<CarbonDioxideLabelDetailComponent />}
					handleOverlayOpen={handleOverlayOpen}
					handleOverlayClose={handleOverlayClose}
				/>
				<br />
				<Pagination
					countOfAllListItems={carbonDioxideLabelList.totalCount}
					pageIndex={search.getPageIndex()}
					setPageIndex={handleChangePageIndex}
					listItemAmount={search.getPageSize()}
					maxNumberOfButtons={maxNumberOfButtons}
				/>
			</main>
		);
	};

	return <>{renderContent()}</>;
};

export default CarbonDioxideLabelManagementComponent;
